// 검차비 변경 — 평가사가 잡기 전(status 'open')에만 보인다. eval 상세(한글)와 간편 모드(t()) 가 공유한다.
// 카드 = 현재 금액 + [검차비 변경]. 시트에서 ±5,000/±10,000 으로 목표 금액을 맞추고(서버 호출 없음)
// [변경 요청] 때 누적 차액으로 한 번만 보낸다 (앱 fee_adjust_card.dart 와 같은 흐름, Jin 10/19).
// 서버: POST /eval/orders/:id/fee-adjust {delta} → 200 {data:<order>} / 409 NOT_OPEN / 400 BELOW_MIN·INVALID_DELTA.
// props: order, fmt(won→문자열), t(key, vars) 문구(생략 시 ko 사전 — eval 상세는 Provider 밖), label(카드 라벨, 기본 simpleFeeLabel),
//        describe(err→문구, 기본 서버 error), onChanged(row) 성공 후 · onNotOpen() 409 후(상세 재조회)
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API, authFetch } from '../../../utils/api';
import { LOCALES } from '../../../i18n/I18nProvider';
import { SheetFrame } from '../../simple/parts/ConfirmSheet';
import { T, S, R, FONT, NUM } from '../../theme';

var STEPS = [-10000, -5000, 5000, 10000];
var wrap = { background: T.bandBg, border: '1px solid ' + T.bandBd, borderRadius: R.card, padding: '10px 14px', marginTop: 12 };
var row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
var labelStyle = { display: 'block', fontSize: FONT.label, color: T.muted };
var amount = Object.assign({ fontSize: 15, fontWeight: 800, color: T.ink }, NUM);
var hintStyle = { fontSize: FONT.label, color: T.muted, marginTop: 6, lineHeight: 1.5 };
var errStyle = { fontSize: FONT.label, color: T.danger, marginTop: 6, lineHeight: 1.5 };
var doneStyle = { fontSize: FONT.label, color: T.ok, marginTop: 6, lineHeight: 1.5, fontWeight: 700 };
var dis = { opacity: 0.45, cursor: 'not-allowed' };
// 시트
var center = { textAlign: 'center' };
var curStyle = { fontSize: 12, color: T.muted, textAlign: 'center' };
var targetStyle = Object.assign({ fontSize: 26, fontWeight: 800, color: T.ink, textAlign: 'center' }, NUM);
var deltaStyle = Object.assign({ fontSize: 12, fontWeight: 700, textAlign: 'center', minHeight: 18 }, NUM);
var steps = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 };
var floorStyle = { fontSize: 11.5, color: T.faint, textAlign: 'center' };
var noteBox = { padding: '10px 12px', borderRadius: R.md, fontSize: 12, color: T.body, lineHeight: 1.55 };
var btnRow = { display: 'flex', gap: 10 };
var btn = { flex: 1, height: 42, borderRadius: R.md, fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 };
var btnGhost = Object.assign({}, btn, { background: T.card, border: '1px solid ' + T.bd, color: T.body });
var btnNavy = Object.assign({}, btn, { background: T.navy, border: '1px solid ' + T.navy, color: T.card, flex: 2 });
// 시트는 body 포털 + AI도우미 버튼(z 9990) 위 레이어 — 상세 패널(transform) 안에 갇히면 화면 전체를 못 덮고
// 챗봇 버튼이 [변경 요청] 을 가린다 (10/19 에뮬 확인).
var sheetLayer = { position: 'relative', zIndex: 100000 };
var btnNavyOff = Object.assign({}, btn, { background: T.track, border: '1px solid ' + T.bd, color: T.faint, flex: 2, cursor: 'not-allowed' });

// Provider 밖(eval 상세) 기본 문구 — ko 사전 + {name} 치환.
function koT(key, vars) {
  var s = LOCALES.ko[key] || key;
  return vars ? s.replace(/\{(\w+)\}/g, function (m, k) { return vars[k] != null ? String(vars[k]) : m; }) : s;
}

function signed(d) { return (d < 0 ? '−' : '+') + Math.abs(d).toLocaleString('en-US'); }

function postDelta(id, delta) {
  return authFetch(API + '/eval/orders/' + encodeURIComponent(id) + '/fee-adjust', { method: 'POST', body: JSON.stringify({ delta: delta }) })
    .then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok || !body || body.success === false) {
          var e = new Error((body && (body.error || body.message)) || ('HTTP ' + res.status));
          e.status = res.status; e.code = (body && body.code) || '';
          throw e;
        }
        return body.data || {};
      });
    });
}

function FeeAdjust(props) {
  var order = props.order;
  var t = props.t || koT;
  var describe = props.describe || function (e) { return e.message; };
  var fee = Number(order.fee) || 0;
  var floor = Number(order.fee_initial != null ? order.fee_initial : order.fee) || 0;
  var stO = useState(false); var open = stO[0], setOpen = stO[1];
  var stT = useState(fee); var target = stT[0], setTarget = stT[1];
  var stB = useState(false); var busy = stB[0], setBusy = stB[1];
  var stE = useState(''); var sheetErr = stE[0], setSheetErr = stE[1];
  var stM = useState({ kind: '', text: '' }); var msg = stM[0], setMsg = stM[1];

  // 완료 문구는 3초 뒤 걷는다 (간편 모드 toast 와 같은 길이).
  useEffect(function () {
    if (msg.kind !== 'done') return undefined;
    var timer = setTimeout(function () { setMsg({ kind: '', text: '' }); }, 3000);
    return function () { clearTimeout(timer); };
  }, [msg, setMsg]);

  if (order.status !== 'open') return null;

  var openSheet = function () { setTarget(fee); setSheetErr(''); setMsg({ kind: '', text: '' }); setOpen(true); };
  var close = function () { if (!busy) setOpen(false); };
  var delta = target - fee;

  var submit = function () {
    if (busy || !delta) return;
    setBusy(true);
    setSheetErr('');
    postDelta(order.id, delta).then(function (row2) {
      setBusy(false);
      setOpen(false);
      setMsg({ kind: 'done', text: t('simpleFeeChanged', { amount: props.fmt(Number(row2.fee)) }) });
      if (props.onChanged) props.onChanged(row2);
    }, function (e) {
      setBusy(false);
      if (e.code === 'NOT_OPEN') {
        // 그 사이 평가사가 잡았다 — 시트를 닫고 카드에 알린 뒤 상세를 다시 읽는다.
        setOpen(false);
        setMsg({ kind: 'err', text: describe(e) });
        if (props.onNotOpen) props.onNotOpen();
        return;
      }
      setSheetErr(describe(e));
    });
  };

  var note = delta > 0 ? t('simpleFeeSheetRaiseNote', { amount: props.fmt(target) })
    : delta < 0 ? t('simpleFeeSheetLowerNote') : t('simpleFeeSheetIdle');
  var canSubmit = !!delta && !busy;

  return (<div style={wrap}>
    <div style={row}>
      <div style={{ marginInlineEnd: 'auto' }}>
        <span style={labelStyle}>{props.label || t('simpleFeeLabel')}</span>
        <span dir="ltr" style={Object.assign({ unicodeBidi: 'isolate' }, amount)}>{props.fmt(fee)}</span>
      </div>
      <button type="button" data-fee-change="" style={S.btn} onClick={openSheet}>{t('simpleFeeChangeAction')}</button>
    </div>
    <div style={hintStyle}>{t('simpleFeeAdjustHint')}</div>
    {msg.kind === 'err' ? <div role="alert" style={errStyle}>{msg.text}</div> : null}
    {msg.kind === 'done' ? <div role="status" style={doneStyle}>{msg.text}</div> : null}

    {open ? createPortal(<div style={sheetLayer}><SheetFrame open title={t('simpleFeeChangeAction')} onClose={close} closeLabel={t('actionClose')}>
      <div style={center}>
        <div style={curStyle}>{t('simpleFeeSheetCurrent', { amount: props.fmt(fee) })}</div>
        <div dir="ltr" data-fee-target="" style={targetStyle}>{props.fmt(target)}</div>
        <div dir="ltr" style={Object.assign({}, deltaStyle, { color: delta < 0 ? T.muted : T.accent })}>{delta ? signed(delta) : ''}</div>
      </div>
      <div dir="ltr" style={steps}>
        {STEPS.map(function (d) {
          var off = busy || (d < 0 && target + d < floor);
          return (<button key={d} type="button" data-delta={d} disabled={off} style={Object.assign({}, S.btn, { justifyContent: 'center' }, off ? dis : null)}
            onClick={function () { setTarget(target + d); setSheetErr(''); }}>{signed(d)}</button>);
        })}
      </div>
      <div style={floorStyle}>{t('simpleFeeSheetFloor', { amount: props.fmt(floor) })}</div>
      <div data-fee-note="" style={Object.assign({}, noteBox, { background: delta > 0 ? T.accentTint : T.bandBg })}>{note}</div>
      {sheetErr ? <div role="alert" style={errStyle}>{sheetErr}</div> : null}
      <div style={btnRow}>
        <button type="button" style={btnGhost} disabled={busy} onClick={close}>{t('actionClose')}</button>
        <button type="button" data-fee-confirm="" style={canSubmit ? btnNavy : btnNavyOff} disabled={!canSubmit} onClick={submit}>
          {busy ? '…' : t('simpleFeeSheetConfirm')}
        </button>
      </div>
    </SheetFrame></div>, document.body) : null}
  </div>);
}

export default FeeAdjust;
